import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  MenuItem,
  TextField,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import DatePicker2 from "../../../../../views/components/common/textFields/DatePicker2";
import { PaymentMode } from "../../../../../models/Invoice/Invoice";
import {
  VendorPaymentRow,
  VendorPaymentTiming,
} from "../../../../../models/PurchaseRequest/VendorPayments";

const PAYMENT_MODE_OPTIONS: { value: PaymentMode; label: string }[] = [
  { value: PaymentMode.KODO, label: "KODO" },
  { value: PaymentMode.CHEQUE, label: "Cheque" },
  { value: PaymentMode.RAZORPAY, label: "Razorpay" },
  { value: PaymentMode.STRIPE, label: "Stripe" },
  { value: PaymentMode.CREDIT_CREDIT, label: "Credit card" },
  { value: PaymentMode.BANK_TRANSFER, label: "Bank transfer" },
  { value: PaymentMode.BANK_REMITTANCE, label: "Bank remittance" },
  { value: PaymentMode.AUTO_TRANSACTION, label: "Auto transaction" },
  { value: PaymentMode.OTHERS, label: "Others" },
];
const PAYMENT_MODE_VALUES = new Set(PAYMENT_MODE_OPTIONS.map((o) => o.value));

interface VendorPaymentPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (payload: {
    amount: number;
    payment_timing: VendorPaymentTiming;
    payment_mode: string | null;
    remarks: string | null;
    payment_date: string | null;
    utr_no: string | null;
  }) => Promise<void>;
  payment?: VendorPaymentRow | null;
  loading?: boolean;
  maxAllowedAmount?: number;
}

const VendorPaymentPopup = ({
  open,
  onClose,
  onSave,
  payment,
  loading = false,
  maxAllowedAmount,
}: VendorPaymentPopupProps) => {
  const theme = useTheme();
  const [amount, setAmount] = useState<string>("");
  const [paymentTiming, setPaymentTiming] =
    useState<VendorPaymentTiming>("advance");
  const [paymentMode, setPaymentMode] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [paymentDate, setPaymentDate] = useState<Dayjs | null>(null);
  const [utrNo, setUtrNo] = useState<string>("");
  const [errors, setErrors] = useState<{
    amount?: string;
    payment_timing?: string;
    payment_date?: string;
    utr_no?: string;
  }>({});
  const [submitError, setSubmitError] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setAmount(payment?.amount?.toString() || "");
    setPaymentTiming(payment?.payment_timing || "advance");
    const rawMode = payment?.payment_mode?.trim() || "";
    setPaymentMode(
      rawMode && PAYMENT_MODE_VALUES.has(rawMode as PaymentMode)
        ? rawMode
        : "",
    );
    setRemarks(payment?.remarks || "");
    setPaymentDate(
      payment?.payment_date ? dayjs(payment.payment_date) : null,
    );
    setUtrNo(payment?.utr_no?.trim() || "");
    setErrors({});
    setSubmitError("");
  }, [open, payment]);

  const isEditMode = useMemo(() => Boolean(payment?.id), [payment?.id]);

  const getAmountError = (value: string) => {
    const parsedAmount = Number(value);
    if (!value || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      return "Enter a valid amount greater than 0";
    }
    if (
      typeof maxAllowedAmount === "number" &&
      parsedAmount > maxAllowedAmount + 0.00001
    ) {
      return "Payment amount cannot be exceeds more than the purchase order amount";
    }
    return "";
  };

  useEffect(() => {
    if (!open) return;
    if (!amount) return;
    const amountError = getAmountError(amount);
    setErrors((prev) => ({
      ...prev,
      amount: amountError || undefined,
    }));
  }, [amount, maxAllowedAmount, open]);

  const validate = () => {
    const nextErrors: {
      amount?: string;
      payment_timing?: string;
      payment_date?: string;
      utr_no?: string;
    } = {};
    const amountError = getAmountError(amount);
    if (amountError) nextErrors.amount = amountError;
    if (!paymentTiming) {
      nextErrors.payment_timing = "Select payment timing";
    }
    if (!paymentDate || !paymentDate.isValid()) {
      nextErrors.payment_date = "Select payment date";
    }
    if (!utrNo.trim()) {
      nextErrors.utr_no = "Enter UTR number";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitError("");
      await onSave({
        amount: Number(amount),
        payment_timing: paymentTiming,
        payment_mode:
          paymentMode && PAYMENT_MODE_VALUES.has(paymentMode as PaymentMode)
            ? paymentMode
            : null,
        remarks: remarks.trim() ? remarks.trim() : null,
        payment_date: paymentDate?.isValid()
          ? paymentDate.format("YYYY-MM-DD")
          : null,
        utr_no: utrNo.trim() ? utrNo.trim() : null,
      });
    } catch (error: any) {
      setSubmitError(
        error?.message || "Failed to save payment. Please try again later.",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow: "0px 24px 40px rgba(15, 23, 42, 0.22)",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(90deg, ${theme.palette.info.main}12 0%, ${theme.palette.primary.main}10 100%)`,
        }}
      >
        <Box>
          <CustomTypography variant="heading5" fontWeight={700}>
            {isEditMode ? "Edit Vendor Payment" : "Add Vendor Payment"}
          </CustomTypography>
          <CustomTypography variant="body2" color="text.secondary">
            Amount, timing, payment date, and UTR for this entry.
          </CustomTypography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Box>
            <CustomTypography variant="heading6" style={{ marginTop: 16, marginBottom: 8 }}>
              Amount
            </CustomTypography>
            <CustomTextField
              width="100%"
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                const amountError = getAmountError(e.target.value);
                setErrors((prev) => ({
                  ...prev,
                  amount: amountError || undefined,
                }));
              }}
              allowNegativeValue={false}
              helperText=""
            />
            {errors.amount ? (
              <Box sx={{ color: theme.palette.error.main, fontSize: "0.8rem", mt: 0.5 }}>
                {errors.amount}
              </Box>
            ) : null}
          </Box>

          <Box>
            <CustomTypography variant="heading6" sx={{ mb: 1 }}>
              Payment Timing
            </CustomTypography>
            <TextField
              select
              fullWidth
              size="small"
              value={paymentTiming}
              onChange={(e) => {
                setPaymentTiming(e.target.value as VendorPaymentTiming);
                if (errors.payment_timing) {
                  setErrors((prev) => ({ ...prev, payment_timing: "" }));
                }
              }}
              error={Boolean(errors.payment_timing)}
              helperText={errors.payment_timing || ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem value="advance">Advance</MenuItem>
              <MenuItem value="post_invoice">Post Invoice</MenuItem>
            </TextField>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <CustomTypography variant="heading6" sx={{ mb: 1 }}>
                Payment Date
              </CustomTypography>
              <DatePicker2
                type="date"
                label=""
                value={paymentDate}
                onChange={(value) => {
                  setPaymentDate(value);
                  if (errors.payment_date) {
                    setErrors((prev) => ({ ...prev, payment_date: undefined }));
                  }
                }}
                error={Boolean(errors.payment_date)}
                borderColor={theme.palette.customColors.lightGrayBorder}
                backgroundColor={theme.palette.customColors.white}
              />
              {errors.payment_date ? (
                <Box
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: "0.8rem",
                    mt: 0.5,
                  }}
                >
                  {errors.payment_date}
                </Box>
              ) : null}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <CustomTypography variant="heading6" sx={{ mb: 1 }}>
                UTR No.
              </CustomTypography>
              <CustomTextField
                width="100%"
                placeholder="Enter UTR / reference number"
                value={utrNo}
                onChange={(e) => {
                  setUtrNo(e.target.value);
                  if (errors.utr_no) {
                    setErrors((prev) => ({ ...prev, utr_no: undefined }));
                  }
                }}
                helperText=""
              />
              {errors.utr_no ? (
                <Box
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: "0.8rem",
                    mt: 0.5,
                  }}
                >
                  {errors.utr_no}
                </Box>
              ) : null}
            </Grid2>
          </Grid2>

          <Box>
            <CustomTypography variant="heading6" sx={{ mb: 1 }}>
              Payment Mode
            </CustomTypography>
            <TextField
              select
              fullWidth
              size="small"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem value="">
                <em>Select payment mode (optional)</em>
              </MenuItem>
              {PAYMENT_MODE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <CustomTypography variant="heading6" sx={{ mb: 1 }}>
              Remarks
            </CustomTypography>
            <TextField
              multiline
              minRows={3}
              fullWidth
              size="small"
              placeholder="Optional notes"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Box>
        </Box>
        {submitError && (
          <CustomTypography
            variant="body2"
            color="error.main"
            sx={{ mt: 1.5, p: 1, backgroundColor: theme.palette.error.main + "10", borderRadius: 1 }}
          >
            {submitError}
          </CustomTypography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.customColors.lightGreyColor,
        }}
      >
        <AppButton
          label="Cancel"
          variant="grayscale"
          onClick={onClose}
          customStyles={{ minWidth: 120 }}
          disabled={loading}
        />
        <AppButton
          label={loading ? "Saving..." : isEditMode ? "Update Payment" : "Add Payment"}
          onClick={handleSubmit}
          customStyles={{ minWidth: 150 }}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default VendorPaymentPopup;
